import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, RefreshCw, Activity } from "lucide-react";
import { useResponsiveTickInterval } from "@/lib/chart-utils";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useKennelStatusTrends } from "@/hooks/use-population-insights";
import { useMemo, useState } from "react";
import { useToggleableSeries, InteractiveLegend, type LegendSeries } from "./interactive-legend";
import { format, parseISO, addDays } from "date-fns";
import {
  type ComparisonMode,
  getComparisonDays,
  getComparisonPeriodLabel,
  LOCATION_SERIES_COLORS,
  formatTrendDate,
} from "@/lib/trend-comparison";

type SpeciesFilter = "all" | "DOG" | "CAT" | "OTHER";

const STATUS_COLORS: Record<string, string> = {
  available: "hsl(142 70% 45%)",
  unavail: "hsl(0 72% 51%)",
  evaluation: "hsl(38 92% 50%)",
  strayWait: "hsl(210 70% 50%)",
  unknown: "hsl(var(--muted-foreground))",
};

const STATUS_LABELS: Record<string, string> = {
  available: "Available",
  unavail: "Unavailable",
  evaluation: "Evaluation",
  strayWait: "Stray Wait",
  unknown: "Unknown",
};

const STATUS_KEYS = ["available", "evaluation", "strayWait", "unavail", "unknown"] as const;

const STATUS_SERIES: LegendSeries[] = STATUS_KEYS.map(key => ({
  key,
  label: STATUS_LABELS[key],
  color: STATUS_COLORS[key],
}));

interface KennelStatusTrendsChartProps {
  days: number;
  showSystem: boolean;
  selectedLocations: string[];
  comparisonMode: ComparisonMode;
  locations: Array<{ code: string; label: string }>;
}

function StatCard({ label, value, color, testId }: { label: string; value: number; color?: string; testId: string }) {
  return (
    <div className="flex flex-col p-3 rounded-md border" data-testid={testId}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold" style={color ? { color } : undefined}>{value}</span>
    </div>
  );
}

interface ChartDataPoint {
  date: string;
  formattedDate: string;
  available: number;
  unavail: number;
  evaluation: number;
  strayWait: number;
  unknown: number;
  total: number;
  _prevTotal?: number | null;
}

function extractSpeciesValues(
  p: any,
  species: SpeciesFilter,
): { available: number; unavail: number; evaluation: number; strayWait: number; unknown: number } {
  if (species === "all") {
    return { available: p.available, unavail: p.unavail, evaluation: p.evaluation, strayWait: p.strayWait, unknown: p.unknown };
  }
  const sp = p.bySpecies?.[species];
  if (!sp) return { available: 0, unavail: 0, evaluation: 0, strayWait: 0, unknown: 0 };
  return { available: sp.available, unavail: sp.unavail, evaluation: sp.evaluation, strayWait: sp.strayWait, unknown: sp.unknown };
}

function SingleLocationChart({
  days,
  location,
  comparisonMode,
  speciesFilter,
  label,
  color,
}: {
  days: number;
  location?: string;
  comparisonMode: ComparisonMode;
  speciesFilter: SpeciesFilter;
  label?: string;
  color?: string;
}) {
  const comparisonDays = getComparisonDays(comparisonMode);
  const fetchDays = comparisonMode !== "none" ? days + comparisonDays : days;
  const { data, isLoading, isError, refetch } = useKennelStatusTrends(fetchDays, location);

  const chartData = useMemo((): ChartDataPoint[] => {
    if (!data?.series) return [];
    const allPoints = data.series.map((p) => {
      const vals = extractSpeciesValues(p, speciesFilter);
      return {
        date: p.date,
        formattedDate: formatTrendDate(p.date),
        ...vals,
        total: vals.available + vals.unavail + vals.evaluation + vals.strayWait + vals.unknown,
      };
    });

    if (comparisonMode === "none" || comparisonDays === 0) {
      return allPoints.slice(-days);
    }

    const fullMap = new Map<string, (typeof allPoints)[0]>();
    for (const row of allPoints) fullMap.set(row.date, row);
    const currentSlice = allPoints.slice(-days);

    return currentSlice.map((row) => {
      const prevDate = format(addDays(parseISO(row.date), -comparisonDays), "yyyy-MM-dd");
      const prevRow = fullMap.get(prevDate);
      return { ...row, _prevTotal: prevRow?.total ?? null };
    });
  }, [data, days, comparisonMode, comparisonDays, speciesFilter]);

  const hasData = chartData.length > 0;
  const hasPrev = comparisonMode !== "none" && chartData.some((d) => d._prevTotal != null);
  const tickInterval = useResponsiveTickInterval(chartData.length);

  const allSeries = useMemo(() => {
    if (!hasPrev) return STATUS_SERIES;
    return [...STATUS_SERIES, { key: "_prevTotal", label: `Prev Total (${getComparisonPeriodLabel(comparisonMode)})`, color: "hsl(var(--foreground))" }];
  }, [hasPrev, comparisonMode]);
  const { visibleSeries, toggleSeries } = useToggleableSeries(allSeries);

  if (isLoading) return <Skeleton className="h-[280px] w-full" />;
  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="flex items-center gap-2">
          Failed to load{label ? ` ${label}` : ""} trends.
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!hasData) {
    return (
      <div className="flex items-center justify-center h-[200px] text-muted-foreground">
        No data available for the selected period.
      </div>
    );
  }

  return (
    <div>
      {label && (
        <p className="text-sm font-medium mb-2" style={color ? { color } : undefined}>
          {label}
        </p>
      )}
      <InteractiveLegend
        series={allSeries}
        visibleSeries={visibleSeries}
        onToggle={toggleSeries}
        compact
        testIdPrefix="kennel-legend"
      />
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="formattedDate" tick={{ fontSize: 12 }} className="text-muted-foreground" interval={tickInterval} />
          <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              borderColor: "hsl(var(--border))",
              borderRadius: "6px",
              color: "hsl(var(--card-foreground))",
            }}
          />
          {STATUS_KEYS.filter(k => visibleSeries.has(k)).map((key) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              name={STATUS_LABELS[key]}
              stackId="1"
              stroke={STATUS_COLORS[key]}
              fill={STATUS_COLORS[key]}
              fillOpacity={key === "unknown" ? 0.3 : 0.6}
            />
          ))}
          {hasPrev && visibleSeries.has("_prevTotal") && (
            <Line
              type="monotone"
              dataKey="_prevTotal"
              name={`Previous Total (${getComparisonPeriodLabel(comparisonMode)})`}
              stroke="hsl(var(--foreground))"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              connectNulls
              legendType="line"
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function KennelChartWithLegend({ chartData, hasPrev, comparisonMode }: { chartData: ChartDataPoint[]; hasPrev: boolean; comparisonMode: ComparisonMode }) {
  const tickInterval = useResponsiveTickInterval(chartData.length);
  const allSeries = useMemo(() => {
    if (!hasPrev) return STATUS_SERIES;
    return [...STATUS_SERIES, { key: "_prevTotal", label: `Prev Total (${getComparisonPeriodLabel(comparisonMode)})`, color: "hsl(var(--foreground))" }];
  }, [hasPrev, comparisonMode]);
  const { visibleSeries, toggleSeries } = useToggleableSeries(allSeries);
  return (
    <>
      <InteractiveLegend
        series={allSeries}
        visibleSeries={visibleSeries}
        onToggle={toggleSeries}
        testIdPrefix="kennel-legend"
      />
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="formattedDate" tick={{ fontSize: 12 }} className="text-muted-foreground" interval={tickInterval} />
          <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              borderColor: "hsl(var(--border))",
              borderRadius: "6px",
              color: "hsl(var(--card-foreground))",
            }}
          />
          {STATUS_KEYS.filter(k => visibleSeries.has(k)).map((key) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              name={STATUS_LABELS[key]}
              stackId="1"
              stroke={STATUS_COLORS[key]}
              fill={STATUS_COLORS[key]}
              fillOpacity={key === "unknown" ? 0.3 : 0.6}
            />
          ))}
          {hasPrev && visibleSeries.has("_prevTotal") && (
            <Line
              type="monotone"
              dataKey="_prevTotal"
              name={`Previous Total (${getComparisonPeriodLabel(comparisonMode)})`}
              stroke="hsl(var(--foreground))"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              connectNulls
              legendType="line"
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </>
  );
}

export function KennelStatusTrendsChart({ days, showSystem, selectedLocations, comparisonMode, locations }: KennelStatusTrendsChartProps) {
  const isComparing = selectedLocations.length > 1 || (!showSystem && selectedLocations.length > 0);
  const showLocationPanels = selectedLocations.length > 0;
  const [speciesFilter, setSpeciesFilter] = useState<SpeciesFilter>("all");

  const location = showSystem ? undefined : (selectedLocations.length === 1 ? selectedLocations[0] : undefined);

  const comparisonDays = getComparisonDays(comparisonMode);
  const fetchDays = comparisonMode !== "none" ? days + comparisonDays : days;
  const { data, isLoading, isError, refetch } = useKennelStatusTrends(fetchDays, location);

  const chartData = useMemo((): ChartDataPoint[] => {
    if (!data?.series) return [];
    const allPoints = data.series.map((p) => {
      const vals = extractSpeciesValues(p, speciesFilter);
      return {
        date: p.date,
        formattedDate: formatTrendDate(p.date),
        ...vals,
        total: vals.available + vals.unavail + vals.evaluation + vals.strayWait + vals.unknown,
      };
    });

    if (comparisonMode === "none" || comparisonDays === 0) {
      return allPoints.slice(-days);
    }

    const fullMap = new Map<string, (typeof allPoints)[0]>();
    for (const row of allPoints) fullMap.set(row.date, row);
    const currentSlice = allPoints.slice(-days);

    return currentSlice.map((row) => {
      const prevDate = format(addDays(parseISO(row.date), -comparisonDays), "yyyy-MM-dd");
      const prevRow = fullMap.get(prevDate);
      return { ...row, _prevTotal: prevRow?.total ?? null };
    });
  }, [data, days, comparisonMode, comparisonDays, speciesFilter]);

  const latest = useMemo(() => {
    if (!data?.latest) return null;
    if (speciesFilter === "all") return data.latest;
    const sp = data.latest.bySpecies?.[speciesFilter];
    if (!sp) return null;
    return {
      available: sp.available,
      unavail: sp.unavail,
      evaluation: sp.evaluation,
      strayWait: sp.strayWait,
      unknown: sp.unknown,
      total: sp.available + sp.unavail + sp.evaluation + sp.strayWait + sp.unknown,
    };
  }, [data, speciesFilter]);

  const hasData = chartData.length > 0;
  const hasPrev = comparisonMode !== "none" && chartData.some((d) => d._prevTotal != null);

  const getLocationLabel = (code: string) => locations.find((l) => l.code === code)?.label || code;

  if (isLoading && !showLocationPanels) {
    return (
      <Card data-testid="card-kennel-status-trends">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Kennel Status Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError && !showLocationPanels) {
    return (
      <Card data-testid="card-kennel-status-trends">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Kennel Status Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center gap-2">
              Failed to load kennel status trends.
              <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-kennel-trends">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card id="section-kennel-status-trends" data-testid="card-kennel-status-trends">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Kennel Status Trends
        </CardTitle>
        <Select value={speciesFilter} onValueChange={(v) => setSpeciesFilter(v as SpeciesFilter)}>
          <SelectTrigger className="w-[140px]" data-testid="select-species-kennel-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Species</SelectItem>
            <SelectItem value="DOG">Dogs</SelectItem>
            <SelectItem value="CAT">Cats</SelectItem>
            <SelectItem value="OTHER">Other</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {!showLocationPanels && latest && (
          <div className="mb-4" data-testid="kennel-trends-stats">
            <p className="text-xs text-muted-foreground mb-1" data-testid="text-kennel-stats-hint">
              All records within selected date range
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
              <StatCard label="Total" value={latest.total} testId="stat-kennel-total" />
              <StatCard label={STATUS_LABELS.available} value={latest.available} color={STATUS_COLORS.available} testId="stat-kennel-available" />
              <StatCard label={STATUS_LABELS.unavail} value={latest.unavail} color={STATUS_COLORS.unavail} testId="stat-kennel-unavail" />
              <StatCard label={STATUS_LABELS.evaluation} value={latest.evaluation} color={STATUS_COLORS.evaluation} testId="stat-kennel-evaluation" />
              <StatCard label={STATUS_LABELS.strayWait} value={latest.strayWait} color={STATUS_COLORS.strayWait} testId="stat-kennel-stray" />
              <StatCard label={STATUS_LABELS.unknown} value={latest.unknown} testId="stat-kennel-unknown" />
            </div>
          </div>
        )}

        {showLocationPanels ? (
          <div className={`grid gap-4 grid-cols-1 md:grid-cols-2`}>
            {showSystem && (
              <SingleLocationChart
                days={days}
                comparisonMode={comparisonMode}
                speciesFilter={speciesFilter}
                label="System-wide"
              />
            )}
            {selectedLocations.map((locCode, idx) => (
              <SingleLocationChart
                key={locCode}
                days={days}
                location={locCode}
                comparisonMode={comparisonMode}
                speciesFilter={speciesFilter}
                label={getLocationLabel(locCode)}
                color={LOCATION_SERIES_COLORS[idx % LOCATION_SERIES_COLORS.length]}
              />
            ))}
          </div>
        ) : hasData ? (
          <KennelChartWithLegend chartData={chartData} hasPrev={hasPrev} comparisonMode={comparisonMode} />
        ) : (
          <div className="flex items-center justify-center h-[200px] text-muted-foreground" data-testid="text-kennel-trends-empty">
            No kennel status trend data available for the selected period.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
